// Story BP-5: paywall modal — apresentação isolada.
// Props tipadas; CTA dispara usePlan().upgrade(tier) (BP-3 já fallbacka
// pra /pricing?intent=upgrade&tier=X quando BP-2 edge function falha).
// Reusa components/ui/Modal.tsx (closeOnEscape/closeOnOverlayClick default).

import React, { useState } from 'react';
import { Sparkles, Loader2, ArrowUpRight } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../Button';
import type { PlanTier, FeatureKey } from '../../lib/plans/types';
import { usePlan } from '../../hooks/usePlan';
import { logService } from '../../lib/logService';

export interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Razão explicativa — 1-2 frases pt-BR */
  reason: string;
  /** Tier que precisa ser atingido pra ter acesso */
  tierRequired: Exclude<PlanTier, 'free'>;
  /** Override do CTA primary (default: "Fazer upgrade pra {tierRequired}") */
  ctaText?: string;
  /** Feature que disparou o gate (pra telemetry + logging) */
  feature?: FeatureKey | string;
}

const TIER_DISPLAY: Record<Exclude<PlanTier, 'free'>, string> = {
  pro: 'Pro',
  business: 'Business',
};

// Bullets canônicos por tier (derivados do EPIC.md tier matrix).
// Hardcoded pra evitar render-time query — estável até tier matrix mudar.
const TIER_BENEFITS: Record<Exclude<PlanTier, 'free'>, string[]> = {
  pro: [
    'Até 25 formulários ativos e 2000 respostas/mês',
    'Exportação de respostas individuais em PDF',
    'Webhooks, notificações por email e redirect URL',
    'Upload de logo customizado e remoção de watermark',
    'Flow branching com condições ilimitadas',
  ],
  business: [
    'Formulários e respostas ilimitados',
    'Relatório analítico agregado em PDF (com gráficos)',
    'Webhooks ilimitados',
    'Até 5 assentos de equipe',
    'Tudo do plano Pro',
  ],
};

export const UpgradeModal: React.FC<UpgradeModalProps> = ({
  isOpen,
  onClose,
  reason,
  tierRequired,
  ctaText,
  feature,
}) => {
  const { tier: currentTier, upgrade } = usePlan();
  const [isUpgrading, setIsUpgrading] = useState(false);
  const tierDisplay = TIER_DISPLAY[tierRequired];
  const benefits = TIER_BENEFITS[tierRequired];

  const handleUpgrade = async () => {
    if (isUpgrading) return;
    setIsUpgrading(true);

    // OQ3: telemetry upgrade_intent
    logService.info('upgrade_intent', `cta_clicked ${feature ?? 'unknown'} → ${tierRequired}`, {
      feature: feature ?? 'unknown',
      tier_current: currentTier,
      tier_required: tierRequired,
      cta_clicked: true,
    });

    // upgrade() retorna { ok: true } (navegou Stripe) ou { ok: false, error } (falhou)
    const result = await upgrade(tierRequired);
    if (!result.ok) {
      logService.error('upgrade_intent.failed', `upgrade result not ok: ${result.error}`, {
        feature: feature ?? 'unknown',
        tier_current: currentTier,
        tier_required: tierRequired,
        upgrade_error: result.error,
      });
      setIsUpgrading(false);
      // Fallback: fecha modal e navega SPA pra /pricing (sem reload).
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      showCloseButton={!isUpgrading}
      closeOnEscape={!isUpgrading}
      closeOnOverlayClick={!isUpgrading}
    >
      <div className="px-2 py-2" aria-labelledby="upgrade-title" aria-describedby="upgrade-reason">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-primary-400" />
          </div>
          <div>
            <h2 id="upgrade-title" className="text-xl font-bold text-white">
              Upgrade para {tierDisplay}
            </h2>
            <p className="text-xs text-neutral-500">Desbloqueie recursos avançados</p>
          </div>
        </div>

        <p id="upgrade-reason" className="text-sm text-neutral-300 mb-5 leading-relaxed">
          {reason}
        </p>

        <div className="mb-6">
          <p className="text-xs font-medium text-neutral-400 uppercase tracking-wide mb-3">
            Benefícios do plano {tierDisplay}
          </p>
          <ul className="space-y-2">
            {benefits.map((benefit, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-neutral-300">
                <span className="text-primary-400 mt-0.5 shrink-0">✓</span>
                <span>{benefit}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex gap-3">
          <Button variant="outline" fullWidth onClick={onClose} disabled={isUpgrading}>
            Cancelar
          </Button>
          <button
            onClick={handleUpgrade}
            disabled={isUpgrading}
            className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 min-h-[48px]"
          >
            {isUpgrading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Redirecionando...
              </>
            ) : (
              <>
                {ctaText ?? `Fazer upgrade pra ${tierDisplay}`}
                <ArrowUpRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};
